<template>
  <div class="dashboard">
    <div class="dashboard__filters mt-24">
      <ez-filter-list
        :filters="listFilters"
        @filterUpdated="updateFilters"
        @resetFilter="resetFilters"
      >
        <v-date-picker
          class="calendar"
          name="date"
          label="Invoice Date"
          hide-label
          v-model="range"
          formKey="filters"
          range-mode
          with-predefined-ranges
          :number-of-calendars="2"
          :select-predefined-range="defaultRangeName"
          @rangeNameChange="changeRangeName"
          @applyDate="loadDateWidgets"
          displayEndOfRange
        />
        <v-select-search
          v-if="groupViewMode"
          class="ml-16 select-filter"
          formKey="filters"
          name="venueId"
          label=""
          :data="venueFilter"
          :placeholder="`All ${$t('global.venues')}`"
          :searchPlaceholder="`Select ${$t('global.venue')}`"
          :selected="listFilters.venueId"
          align-left
        />
        <v-select-search
          class="ml-16 select-filter"
          formKey="filters"
          name="distributorId"
          label=""
          :data="distributors"
          :placeholder="`All ${$t('global.distributors')}`"
          :searchPlaceholder="`Select ${$t('global.distributor')}`"
          :selected="listFilters.distributorId"
          align-left
        >
          <template #result="{ result }">
            <v-distributor-entity-info :distributor="result" />
          </template>
        </v-select-search>
      </ez-filter-list>
    </div>

    <div class="dashboard__stats mt-24">
      <template v-if="loadingStats">
        <v-dashboard-stat-box v-for="n in 4" :key="`stat-skeleton-${n}`" loading />
      </template>
      <template v-else>
        <v-dashboard-stat-box
          v-for="metric in metrics"
          :key="metric.key"
          :label="metric.label"
          :value="metric.value"
          :previous-value="hasComparison ? metric.previousValue : null"
          :format="metric.format"
          :comparison-label="comparisonLabel"
          :invert-trend="metric.invertTrend"
        />
      </template>
    </div>

    <div class="dashboard__charts mt-24">
      <div class="dashboard__card">
        <h2 class="dashboard__card-title">Invoiced by Product Group</h2>
        <v-donut-chart
          chart-id="dashboard-product-groups"
          :labels="productGroupLabels"
          :values="productGroupValues"
          :value-formatter="formatCurrency"
          :loading="loadingProductGroups"
          empty-label="No product groups to show"
        />
      </div>
      <div class="dashboard__card">
        <h2 class="dashboard__card-title">Food Cost vs Prior Year</h2>
        <v-line-chart
          chart-id="dashboard-food-cost"
          :labels="foodCostLabels"
          :datasets="foodCostDatasets"
          :value-formatter="formatCurrency"
          :loading="loadingFoodCost"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import dayjs from 'dayjs';
import EzFilterList from '@/components/ui/FilterList';
import VDatePicker from '@/components/v3/patterns/VDatePicker/index';
import VSelectSearch from '@/components/v3/patterns/VSelectSearch';
import VDistributorEntityInfo from '@/components/v3/patterns/VDistributorEntityInfo';
import VDashboardStatBox from '@/components/v3/elements/VDashboardStatBox';
import VDonutChart from '@/components/v3/elements/VDonutChart';
import VLineChart from '@/components/v3/elements/VLineChart';
import {
  getThisMonthRange,
  getLastWeekRange,
  getLastMonthRange,
  getLastYearRange,
} from '@/util/utils-date';
import { debounce } from '@/util/utils';

const DEFAULT_RANGE_NAME = 'This Month';
const DATE_FORMAT = 'YYYY-MM-DD';

export default {
  components: {
    EzFilterList,
    VDatePicker,
    VSelectSearch,
    VDistributorEntityInfo,
    VDashboardStatBox,
    VDonutChart,
    VLineChart,
  },
  data() {
    const { start, end } = getThisMonthRange();
    return {
      range: { start, end },
      rangeName: DEFAULT_RANGE_NAME,
      defaultRangeName: DEFAULT_RANGE_NAME,
      listFilters: {
        venueId: null,
        distributorId: null,
      },
      distributors: [],
      metrics: [],
      productGroups: [],
      foodCost: [],
      loadingStats: false,
      loadingProductGroups: false,
      loadingFoodCost: false,
    };
  },
  computed: {
    ...mapState('entities/users', ['loggedUser', 'contextId']),
    groupViewMode() {
      return this.loggedUser.context === 'venue-group';
    },
    venueFilter() {
      return this.loggedUser.venues || [];
    },
    isAllTime() {
      return this.rangeName === 'All Time';
    },
    comparison() {
      const today = new Date();
      const build = (start, end, label) => ({
        from: dayjs(start).startOf('d').format(DATE_FORMAT),
        to: dayjs(end).endOf('d').format(DATE_FORMAT),
        label,
      });
      switch (this.rangeName) {
        case 'All Time':
          return null;
        case 'Today': {
          const yesterday = dayjs(today).subtract(1, 'd');
          return build(yesterday, yesterday, 'yesterday');
        }
        case 'This Week': {
          const { start, end } = getLastWeekRange(today);
          return build(start, end, 'last week');
        }
        case 'This Month': {
          const { start, end } = getLastMonthRange(today);
          return build(start, end, 'last month');
        }
        case 'This Year': {
          const { start, end } = getLastYearRange(today);
          return build(start, end, 'last year');
        }
        case 'Last Week': {
          const { start, end } = getLastWeekRange(dayjs(today).subtract(1, 'w').toDate());
          return build(start, end, 'previous week');
        }
        case 'Last Month': {
          const { start, end } = getLastMonthRange(dayjs(today).subtract(1, 'M').toDate());
          return build(start, end, 'previous month');
        }
        case 'Last Year': {
          const { start, end } = getLastYearRange(dayjs(today).subtract(1, 'y').toDate());
          return build(start, end, 'previous year');
        }
        default: {
          const start = dayjs(this.range.start).startOf('d');
          const end = dayjs(this.range.end).endOf('d');
          const days = end.diff(start, 'd') + 1;
          const previousEnd = start.subtract(1, 'd').endOf('d');
          const previousStart = previousEnd.subtract(days - 1, 'd').startOf('d');
          return build(previousStart, previousEnd, 'previous period');
        }
      }
    },
    hasComparison() {
      return this.comparison !== null;
    },
    comparisonLabel() {
      return this.comparison ? this.comparison.label : '';
    },
    dateQuery() {
      if (this.isAllTime) return {};
      return {
        from: dayjs(this.range.start).format(DATE_FORMAT),
        to: dayjs(this.range.end).format(DATE_FORMAT),
      };
    },
    compareQuery() {
      if (!this.comparison) return {};
      return { compareFrom: this.comparison.from, compareTo: this.comparison.to };
    },
    scopeQuery() {
      return {
        ...(this.listFilters.venueId ? { venueId: this.listFilters.venueId } : {}),
        ...(this.listFilters.distributorId ? { distributorId: this.listFilters.distributorId } : {}),
      };
    },
    productGroupLabels() {
      return this.productGroups.map(group => group.name);
    },
    productGroupValues() {
      return this.productGroups.map(group => group.value);
    },
    foodCostLabels() {
      return this.foodCost.map(month => dayjs(month.month).format('MMM YY'));
    },
    foodCostDatasets() {
      return [
        { label: 'This year', data: this.foodCost.map(month => month.value) },
        { label: 'Prior year', data: this.foodCost.map(month => month.previousValue) },
      ];
    },
  },
  methods: {
    ...mapActions('entities/venues', [
      'venueFetchDistributorFilterOptions',
      'venueFetchDashboardStats',
      'venueFetchDashboardProductGroups',
      'venueFetchDashboardFoodCost',
    ]),
    formatCurrency(value) {
      return this.$options.filters.price(value);
    },
    changeRangeName(rangeName) {
      this.rangeName = rangeName;
    },
    async fetchDistributors() {
      const { data } = await this.venueFetchDistributorFilterOptions({
        venueId: this.contextId,
        query: { for: 'products' },
      });
      this.distributors = data.data;
    },
    async loadStats() {
      this.loadingStats = true;
      try {
        const { data } = await this.venueFetchDashboardStats({
          ...this.dateQuery,
          ...this.compareQuery,
          ...this.scopeQuery,
        });
        this.metrics = data.data;
      } finally {
        this.loadingStats = false;
      }
    },
    async loadProductGroups() {
      this.loadingProductGroups = true;
      try {
        const { data } = await this.venueFetchDashboardProductGroups({
          ...this.dateQuery,
          ...this.scopeQuery,
          limit: 10,
        });
        this.productGroups = data.data;
      } finally {
        this.loadingProductGroups = false;
      }
    },
    async loadFoodCost() {
      this.loadingFoodCost = true;
      try {
        const { data } = await this.venueFetchDashboardFoodCost({ ...this.scopeQuery });
        this.foodCost = data.data;
      } finally {
        this.loadingFoodCost = false;
      }
    },
    loadDateWidgets() {
      this.loadStats();
      this.loadProductGroups();
    },
    loadAll() {
      this.loadDateWidgets();
      this.loadFoodCost();
    },
    updateFilters: debounce(function updateFilters(name, value) {
      if (name === 'date') return;
      const id = value && typeof value === 'object' ? value.id : value;
      this.listFilters = { ...this.listFilters, [name]: id || null };
      this.loadAll();
    }, 300),
    resetFilters() {
      this.listFilters = { venueId: null, distributorId: null };
      const { start, end } = getThisMonthRange();
      this.range = { start, end };
      this.rangeName = DEFAULT_RANGE_NAME;
      this.defaultRangeName = DEFAULT_RANGE_NAME;
      this.loadAll();
    },
  },
  created() {
    this.fetchDistributors();
    this.loadAll();
  },
};
</script>

<style scoped lang="scss">
.dashboard {
  &__filters {
    display: flex;
    align-items: center;
  }

  &__stats {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 16px;
  }

  &__charts {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
  }

  &__card {
    background-color: #fff;
    border: 1px solid #e9ebf2;
    border-radius: 6px;
    padding: 20px;
    min-height: 340px;
  }

  &__card-title {
    @include font-size(16px, 24px);
    font-weight: 600;
    color: $color-gray-25;
    margin: 0 0 16px;
  }
}

:deep() .calendar .date-picker .placeholder {
  min-width: 128px;
}

.select-filter {
  min-width: 180px;
}

@media (max-width: 1024px) {
  .dashboard {
    &__stats {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    &__charts {
      grid-template-columns: minmax(0, 1fr);
    }
  }
}

@media (max-width: 556px) {
  .dashboard {
    &__filters {
      flex-wrap: wrap;
      gap: 12px 0;
    }

    &__stats {
      grid-template-columns: minmax(0, 1fr);
    }
  }
}
</style>
